import { GetContract } from '@/services/fhir/contract/GetContract';
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useOrganizationContext } from './OrganizationContext';
import { Contract } from 'fhir/r5';

type ContractResource = Contract;

interface DiscussionCaseContextValue {
  contractDiscussionCase: ContractResource | null;
  provider: 'openai' | 'gemini' | 'anthropic' | null;
  apiKey: string | null;
  refetchContract: () => Promise<void>;
}

interface DiscussionCaseContextProviderProps {
  children: React.ReactNode;
}

export const DiscussionCaseContext = createContext<DiscussionCaseContextValue>({} as DiscussionCaseContextValue);

export function DiscussionCaseContextProvider({ children }: DiscussionCaseContextProviderProps) {
  const { organization } = useOrganizationContext();
  const [contractDiscussionCase, setContractDiscussionCase] = useState<ContractResource | null>(null);
  const [provider, setProvider] = useState<'openai' | 'gemini' | 'anthropic' | null>(null);
  const [apiKey, setApiKey] = useState<string | null>(null);

  const fetchContract = useCallback(async () => {
    try {
      if (!organization?.id) {
        console.warn("Nenhuma organização selecionada");
        setContractDiscussionCase(null);
        setProvider(null);
        setApiKey(null);
        return;
      }

      const contractData = await GetContract({
        organization: organization.id,
        count: 1,
        sort: true,
        status: "executed",
        identifier: "DISCUSSION_CASE_AGENT",
      });
      
      if (contractData?.entry?.[0]?.resource) {
        const resource = contractData.entry[0].resource;
        setContractDiscussionCase(resource);

        const apiKeyExtension = resource.extension?.find(
          (ext: any) => ext.url === "https://fluxmed.com.br/fhir/extension/discussion_case_agent/api-key"
        );
        const providerExtension = resource.extension?.find(
          (ext: any) => ext.url === "https://fluxmed.com.br/fhir/extension/discussion_case_agent/provider"
        );

        if (apiKeyExtension?.valueString && providerExtension?.valueString) {
          setProvider(providerExtension.valueString as 'openai' | 'gemini' | 'anthropic');
          setApiKey(apiKeyExtension.valueString);
        } else {
          setProvider(null);
          setApiKey(null);
        }
      } else {
        setContractDiscussionCase(null);
        setProvider(null);
        setApiKey(null);
      }
    } catch (error) {
      console.error("Erro ao buscar contrato:", error);
      setContractDiscussionCase(null);
      setProvider(null);
      setApiKey(null);
    }
  }, [organization]);

  useEffect(() => {
    fetchContract();
  }, [fetchContract]);

  const contextValue: DiscussionCaseContextValue = {
    contractDiscussionCase,
    provider,
    apiKey,
    refetchContract: fetchContract,
  };

  return (
    <DiscussionCaseContext.Provider value={contextValue}>
      {children}
    </DiscussionCaseContext.Provider>
  );
}

export function useDiscussionCaseContext() {
  return useContext(DiscussionCaseContext);
} 